/** @jsx jsx */
import { Fragment } from 'react';

import { css, jsx } from '@emotion/react';

import { Box, Stack } from '@atlaskit/primitives';
import { media, UNSAFE_media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { TransformedTokenGrouped } from '../types';
import { getTokenListColumnNames } from '../utils';

import TokenButtonValue from './token-button-value';
import TokenDescription from './token-description';
import TokenLifecycle from './token-lifecycle';
import TokenName from './token-name';

interface TokenDefinitionProps {
  token?: TransformedTokenGrouped;
  shouldHideDescription?: boolean;
  isLoading?: boolean;
  testId?: string;
}

const tokenCellStyles = css({
  width: '80%',
  [UNSAFE_media.below.sm]: {
    width: '100%',
  },
});

const tokenValueLabelStyles = css({
  display: 'inline-block',
  color: token('color.text.subtlest', '#626F86'),
  fontSize: token('font.size.075', '12px'),
  fontWeight: token('font.weight.regular', '400'),
  lineHeight: token('font.lineHeight.100', '16px'),
  marginBlockEnd: token('space.050', '4px'),
  [media.above.md]: {
    display: 'none',
  },
});

const resetTableDefaultStyles = css({
  padding: 0,
});

/**
 * Represents a single token consisting of token name, description, and its value(s).
 *
 * Values can be a single value for spacing, or two values (light and dark) for color.
 *
 * For tokens with extensions, the description for the token grouping is rendered by `TokenRow` for the whole grouping (rather than for each definition).
 */
const TokenDefinition = ({
  token,
  shouldHideDescription,
  isLoading,
  testId,
}: TokenDefinitionProps) => {
  if (!token) {
    return null;
  }

  const group = token.attributes.group;
  const isNumericToken = group === 'spacing' || group === 'shape';
  const columnNames = getTokenListColumnNames(group);

  const pixelValue =
    token.attributes.state === 'active' && isNumericToken && token.value;

  const tokenValues = isNumericToken
    ? [
        {
          name: columnNames[1],
          value: pixelValue,
          attributes: token.attributes,
          original: token.original,
          testId: `${testId}-token-item-value-${
            isLoading || pixelValue === undefined ? 'loading' : pixelValue
          }`,
        },
      ]
    : [
        {
          name: columnNames[1],
          value: token.value,
          attributes: token.attributes,
          original: token.original,
          testId: `${testId}-token-item-value-${
            isLoading || pixelValue === undefined
              ? 'loading'
              : token.original.value
          }`,
        },
        {
          name: columnNames[2],
          value: token.darkToken?.value,
          attributes: token.darkToken?.attributes,
          original: token.darkToken?.original,
          testId: `${testId}-token-item-value-${
            isLoading || pixelValue === undefined
              ? 'loading'
              : token.darkToken?.original.value
          }`,
        },
      ];

  return (
    <Fragment>
      <td css={[resetTableDefaultStyles, tokenCellStyles]}>
        <Box>
          <Stack space="space.150">
            <Stack space="space.100">
              <TokenName name={token.name} attributes={token.attributes} />
              {!shouldHideDescription && (
                <TokenDescription transformedToken={token} />
              )}
            </Stack>
            {!shouldHideDescription && (
              <TokenLifecycle transformedToken={token} />
            )}
          </Stack>
        </Box>
      </td>
      {tokenValues.map(tokenValue => (
        <td key={tokenValue.name} css={resetTableDefaultStyles}>
          <Box>
            <span css={tokenValueLabelStyles}>{tokenValue.name}</span>
            <TokenButtonValue
              isFixedWidth
              value={tokenValue.value}
              attributes={tokenValue.attributes!}
              original={tokenValue.original!}
              testId={tokenValue.testId}
            />
          </Box>
        </td>
      ))}
    </Fragment>
  );
};

export default TokenDefinition;
